import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { initMessaging, getToken, onMessage } from "../config/firebase.config";
import {
  registerDeviceToken,
  fetchNotifications,
} from "../Store/Actions/NotificationActions";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Module-level set so it persists across re-renders and component remounts
const toastedIds = new Set();

export function useFCM() {
  const dispatch = useDispatch();
  const loginSuccess = useSelector((state) => state.auth?.loginSuccess);
  const notifications = useSelector(
    (state) => state.notifications?.notifications || []
  );
  const initialized = useRef(false);
  const isReadyRef = useRef(false);

  const isAuthenticated =
    !!loginSuccess || !!localStorage.getItem("accessToken");

  const roleName = loginSuccess?.data?.roleName;
  const assistantPermissions = loginSuccess?.data?.assistantPermissions;
  const notificationsAllowed = roleName !== "assistant" || assistantPermissions?.notifications === true;

  // Track new unread notifications to trigger badge animation
  useEffect(() => {
    if (!isReadyRef.current) return;
    notifications
      .filter((n) => !n.isRead && !toastedIds.has(n.id))
      .forEach((n) => toastedIds.add(n.id));
  }, [notifications]);

  useEffect(() => {
    if (!isAuthenticated || !notificationsAllowed || initialized.current) return;

    const init = async () => {
      try {
        const messaging = await initMessaging();
        if (!messaging) return;

        const permission = await Notification.requestPermission();
        console.log(`[FCM] Notification permission: ${permission}`);
        if (permission !== "granted") {
          console.warn("[FCM] Permission denied — push notifications disabled");
          return;
        }

        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js"
        );

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (token) {
          dispatch(registerDeviceToken(token));
          console.log("[FCM] Push notifications enabled ✓");
        } else {
          console.warn(
            "[FCM] No token returned — check VAPID key and Firebase config"
          );
        }

        // Foreground message: refresh from DB so the notification has its real ID.
        // The useEffect above handles the toast.
        onMessage(messaging, (payload) => {
          dispatch(fetchNotifications(1, 20));
        });

        // Initial fetch: load existing notifications and mark all current unread
        // as already seen so we don't toast for them on page load.
        await dispatch(async (thunkDispatch, getState) => {
          await thunkDispatch(fetchNotifications(1, 20));
          const existing = getState().notifications?.notifications || [];
          existing.filter((n) => !n.isRead).forEach((n) => toastedIds.add(n.id));
          isReadyRef.current = true;
        });

        // Background message from SW: just refresh the list.
        const swMessageHandler = (event) => {
          if (event.data?.type === "FCM_BACKGROUND_MESSAGE") {
            dispatch(fetchNotifications(1, 20));
          }
        };
        navigator.serviceWorker.addEventListener("message", swMessageHandler);

        // Tab becomes visible: refresh in case notifications arrived while away.
        const visibilityHandler = () => {
          if (document.visibilityState === "visible") {
            dispatch(fetchNotifications(1, 20));
          }
        };
        document.addEventListener("visibilitychange", visibilityHandler);

        initialized.current = true;
      } catch (err) {
        console.error("FCM init error:", err);
      }
    };

    init();
  }, [isAuthenticated, notificationsAllowed, dispatch]);
}
